import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { compressImage, canonicalApplianceType, locationForApplianceType } from "@/lib/compress";
import { Camera, X, CheckCircle, Trash, Warning, Sparkle } from "@phosphor-icons/react";

const APPLIANCE_TYPES = [
  "Refrigerator", "Range / Stove", "Oven", "Cooktop", "Microwave", "Dishwasher",
  "Washer", "Dryer", "Water Heater", "Furnace", "AC Condenser", "HVAC Air Handler",
  "Garbage Disposal", "Range Hood", "Ceiling Fan", "Smoke Detector", "Other",
];

const LOCATION_PRESETS = [
  "Kitchen", "Laundry Room", "Bathroom", "Basement", "Garage",
  "Utility", "Attic", "Hallway", "Living Room", "Bedroom",
];

const SCAN_CONCURRENCY = 3;

// One entry per picked photo. Goes through: pending → scanning → ready | failed.
// After user reviews, save() posts each ready entry to /jobs/{id}/appliance.
export default function BulkApplianceScan({ jobId, onClose, onSaved }) {
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const [rows, setRows] = useState([]); // {id, file, preview, status, type, brand, model, serial, location, notes}
  const [saving, setSaving] = useState(false);

  useEffect(() => () => {
    // Free object URLs on unmount
    rows.forEach(r => r.preview && URL.revokeObjectURL(r.preview));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanOne = async (row) => {
    setRows(prev => prev.map(r => r.id === row.id ? { ...r, status: "scanning" } : r));
    try {
      const compressed = await compressImage(row.file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rawType = data?.type || "";
      const type = canonicalApplianceType(rawType);
      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r,
        status: "ready",
        type,
        brand: data?.make || "",
        model: data?.model || "",
        serial: data?.serial || "",
        location: locationForApplianceType(type || rawType),
        notes: data?.notes || "",
        scanned_photo_path: data?.photo_path || "",
      } : r));
    } catch (e) {
      setRows(prev => prev.map(r => r.id === row.id ? {
        ...r,
        status: "failed",
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message || "Scan failed",
        // Still let the user save manually with just a location guess
        location: locationForApplianceType(""),
      } : r));
    }
  };

  const runScans = async (queue) => {
    // Concurrency-limited parallel scans so Claude isn't hammered with 20 requests at once
    const remaining = [...queue];
    await Promise.all(
      Array.from({ length: Math.min(SCAN_CONCURRENCY, remaining.length) }, async () => {
        while (remaining.length) {
          const row = remaining.shift();
          if (row) await scanOne(row);
        }
      })
    );
  };

  const onPickFiles = async (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const additions = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      preview: URL.createObjectURL(f),
      status: "pending",
      type: "", brand: "", model: "", serial: "",
      location: "Kitchen", notes: "",
    }));
    setRows(prev => [...prev, ...additions]);
    // fire scans without blocking UI
    runScans(additions);
  };

  const remove = (id) => {
    setRows(prev => {
      const target = prev.find(r => r.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter(r => r.id !== id);
    });
  };
  const retry = (row) => scanOne(row);
  const update = (id, patch) => setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const saveAll = async () => {
    const ready = rows.filter(r => r.status !== "scanning");
    // Require at least type/brand/model/serial per row (backend also validates)
    const invalid = ready.filter(r => !(r.type || r.brand || r.model || r.serial));
    if (invalid.length) {
      toast.error(`${invalid.length} appliance(s) missing type/brand/model — fill any one field or remove.`);
      return;
    }
    if (ready.length === 0) { toast.error("Nothing to save"); return; }
    setSaving(true);
    let ok = 0, fail = 0;
    // Post in parallel; 5 at a time is comfortable for the API
    const queue = [...ready];
    await Promise.all(
      Array.from({ length: Math.min(5, queue.length) }, async () => {
        while (queue.length) {
          const r = queue.shift();
          if (!r) break;
          try {
            await api.post(`/jobs/${jobId}/appliance`, {
              type: r.type || "",
              brand: r.brand || "",
              model: r.model || "",
              serial: r.serial || "",
              location: r.location || "Kitchen",
              notes: r.notes || "",
              save_to_property: true,
            });
            ok++;
          } catch {
            fail++;
          }
        }
      })
    );
    setSaving(false);
    if (fail === 0) toast.success(`Saved ${ok} appliance${ok === 1 ? "" : "s"}`);
    else toast.warning(`Saved ${ok}, ${fail} failed`);
    await onSaved?.();
    onClose?.();
  };

  const scanningCount = rows.filter(r => r.status === "scanning").length;
  const readyCount = rows.filter(r => r.status === "ready").length;
  const failedCount = rows.filter(r => r.status === "failed").length;

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] my-6"
        onClick={e => e.stopPropagation()} data-testid="bulk-appliance-modal">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a] bg-gradient-to-r from-violet-600 to-[#00C4CC]">
          <div className="text-white">
            <div className="font-display font-black uppercase text-lg tracking-tight">Bulk Appliance Scan</div>
            <div className="text-[11px] font-medium opacity-90">
              Pick every data-plate photo · we auto-detect type + room · one tap to save all
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="bulk-appliance-close"
            className="h-9 w-9 border-2 border-white text-white font-black hover:bg-white/10"><X weight="bold" size={16}/></button>
        </div>

        <div className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} data-testid="bulk-appliance-pick"
              className="tactile-btn h-12 px-5 bg-[#0f172a] text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
              <Camera weight="fill" size={16}/> {rows.length === 0 ? "Take Photos" : "Add More (Camera)"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" className="hidden"
              data-testid="bulk-appliance-input"
              onChange={(e) => { onPickFiles(e.target.files); e.target.value = ""; }}/>
            <button onClick={() => galleryRef.current?.click()} data-testid="bulk-appliance-gallery"
              className="tactile-btn h-12 px-4 bg-white border-2 border-[#0f172a] text-[#0f172a] font-black uppercase text-xs tracking-widest inline-flex items-center gap-2">
              🖼 From Gallery
            </button>
            <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden"
              data-testid="bulk-appliance-gallery-input"
              onChange={(e) => { onPickFiles(e.target.files); e.target.value = ""; }}/>
            {rows.length > 0 && (
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500">
                {rows.length} photo{rows.length === 1 ? "" : "s"}
                {scanningCount > 0 && <span className="ml-1 text-violet-600 animate-pulse">· AI reading {scanningCount}…</span>}
                {readyCount > 0 && <span className="ml-1 text-emerald-600">· {readyCount} ready</span>}
                {failedCount > 0 && <span className="ml-1 text-amber-600">· {failedCount} need review</span>}
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="mt-6 p-8 border-2 border-dashed border-slate-300 text-center" data-testid="bulk-appliance-empty">
              <Sparkle size={40} weight="fill" className="mx-auto text-[#00C4CC]"/>
              <div className="mt-2 font-display font-black text-lg uppercase">Snap every appliance data-plate in a row</div>
              <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
                Fridge → kitchen. Washer → laundry. Water heater → basement. AI reads every plate at once and pre-fills the room, so you keep moving through the unit.
              </p>
            </div>
          ) : (
            <div className="mt-4 space-y-2 max-h-[55vh] overflow-y-auto pr-1" data-testid="bulk-appliance-list">
              {rows.map(r => (
                <ApplianceRow key={r.id} row={r} onChange={update} onRemove={remove} onRetry={retry}/>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t-2 border-[#0f172a] bg-slate-50 flex flex-wrap items-center justify-between gap-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Sparkle weight="fill" size={11} className="text-[#00C4CC]"/> Photos are compressed on your phone before upload
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-white">
              Cancel
            </button>
            <button onClick={saveAll} disabled={saving || rows.length === 0 || scanningCount > 0}
              data-testid="bulk-appliance-save-all"
              className="tactile-btn h-11 px-5 bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
              <CheckCircle weight="fill" size={14}/>
              {saving ? "Saving…" : scanningCount > 0 ? `Scanning ${scanningCount}…` : `Save All (${rows.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApplianceRow({ row, onChange, onRemove, onRetry }) {
  const set = (k) => (e) => onChange(row.id, { [k]: e.target.value });

  const border =
    row.status === "scanning" ? "border-violet-500 animate-pulse" :
    row.status === "failed" ? "border-amber-500" :
    "border-[#0f172a]";

  return (
    <div className={`flex gap-3 p-2 bg-white border-2 ${border}`} data-testid={`bulk-appliance-row-${row.id}`}>
      <div className="relative w-20 h-20 flex-shrink-0 bg-slate-100 border-2 border-slate-300 overflow-hidden">
        <img src={row.preview} alt="" className="w-full h-full object-cover"/>
        {row.status === "scanning" && (
          <div className="absolute inset-0 bg-violet-500/30 flex items-center justify-center">
            <Sparkle weight="fill" size={20} className="text-white animate-pulse"/>
          </div>
        )}
        {row.status === "failed" && (
          <div className="absolute inset-0 bg-amber-500/60 flex items-center justify-center">
            <Warning weight="fill" size={20} className="text-white"/>
          </div>
        )}
        {row.status === "ready" && (
          <div className="absolute bottom-0 right-0 bg-emerald-500 text-white p-0.5">
            <CheckCircle weight="fill" size={12}/>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="col-span-2 md:col-span-1">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Type</div>
          <select value={row.type} onChange={set("type")} data-testid={`bulk-app-type-${row.id}`}
            className="w-full h-9 px-2 border-2 border-slate-300 text-xs font-medium bg-white">
            <option value="">— Pick —</option>
            {APPLIANCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            {row.type && !APPLIANCE_TYPES.includes(row.type) && <option value={row.type}>{row.type} (AI)</option>}
          </select>
        </label>
        <label>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Location</div>
          <select value={row.location} onChange={set("location")} data-testid={`bulk-app-loc-${row.id}`}
            className="w-full h-9 px-2 border-2 border-slate-300 text-xs font-medium bg-white">
            {LOCATION_PRESETS.map(l => <option key={l} value={l}>{l}</option>)}
            {row.location && !LOCATION_PRESETS.includes(row.location) && <option value={row.location}>{row.location}</option>}
          </select>
        </label>
        <label>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Brand</div>
          <input value={row.brand} onChange={set("brand")} data-testid={`bulk-app-brand-${row.id}`}
            placeholder="Whirlpool" className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
        </label>
        <label>
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Model</div>
          <input value={row.model} onChange={set("model")} data-testid={`bulk-app-model-${row.id}`}
            placeholder="Model #" className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
        </label>
        <label className="col-span-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">Serial</div>
          <input value={row.serial} onChange={set("serial")} data-testid={`bulk-app-serial-${row.id}`}
            placeholder="Serial #" className="w-full h-9 px-2 border-2 border-slate-300 text-xs"/>
        </label>
        {row.status === "failed" && (
          <div className="col-span-2 md:col-span-4 flex items-center gap-2 text-[10px] font-medium text-amber-700">
            <Warning size={12} weight="fill"/> {row.error || "Scan failed"}
            <button onClick={() => onRetry(row)} className="ml-auto text-cyan-600 font-black underline">
              Retry
            </button>
          </div>
        )}
      </div>
      <button onClick={() => onRemove(row.id)} data-testid={`bulk-app-remove-${row.id}`}
        className="h-9 w-9 border-2 border-red-500 text-red-500 hover:bg-red-50 flex items-center justify-center flex-shrink-0">
        <Trash weight="bold" size={13}/>
      </button>
    </div>
  );
}
